import { Controller } from '@hotwired/stimulus';
import Offcanvas from 'bootstrapV5/js/dist/offcanvas';
import consumer from '../channels/cable';
import { initSignature } from '../lib/universign';

export default class extends Controller {
  static targets = [
    'block',
    'drawer',
    'informationDrawer',
    'generationStep',
    'signatureStep',
    'successStep',
    'errorStep',
  ];
  static values = {
    universignEndpoint: String,
    createUrl: String,
    refreshUrl: String,
    type: String,
    iframeContainerId: String,
    expanded: { type: Boolean, default: false },
    autoOpen: { type: Boolean, default: false },
    openInformationDrawer: { type: Boolean, default: false },
  };

  connect() {
    this.boundOnMessage = this.#onPostMessage.bind(this);
    window.addEventListener('message', this.boundOnMessage);

    // Drawers opened from the script have no trigger to give the focus back to, so it goes back to the block
    this.boundOnDrawerHidden = this.#onDrawerHidden.bind(this);
    this.#drawers().forEach((drawer) => drawer.addEventListener('hidden.bs.offcanvas', this.boundOnDrawerHidden));

    if (this.openInformationDrawerValue) {
      this.#openInformationDrawer();
    } else if (this.autoOpenValue) {
      this.#removeSignatureParam();
      this.open();
    }
  }

  disconnect() {
    window.removeEventListener('message', this.boundOnMessage);
    this.#drawers().forEach((drawer) => drawer.removeEventListener('hidden.bs.offcanvas', this.boundOnDrawerHidden));
    this.#unsubscribe();
    this.#getOffcanvas()?.hide();
    this.expandedValue = false;
    this.drawerTarget?.classList.remove('offcanvas-end-wide');
  }

  // ACTIONS

  open(event) {
    event?.preventDefault();

    this.#showStep('generation');
    this.#getOffcanvas().show();

    fetch(this.createUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      credentials: 'same-origin',
    })
      // A refused signature answers with its reason, which is shown to the user
      .then((response) => {
        if (!response.ok && response.status !== 422) {
          throw new Error('tax-exemption-signature create returned ' + response.status);
        }

        return response.json();
      })
      .then((data) => {
        if (data.error) {
          this.#showStep('error');
          window.toastr?.error(data.error);

          return;
        }

        this.#subscribe(data.id, data.signature_token);
      })
      .catch((err) => {
        console.error('Unable to start signature', err);
        this.#showStep('error');
      });
  }

  close(event) {
    event?.preventDefault();
    this.#getOffcanvas()?.hide();
    this.#unsubscribe();
  }

  toggleSize(event) {
    event?.preventDefault();
    this.expandedValue = !this.expandedValue;
    this.drawerTarget.classList.toggle('offcanvas-end-wide', this.expandedValue);
  }

  // PRIVATE

  #subscribe(taxExemptionId, signatureToken) {
    this.#unsubscribe();

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'TaxExemptionSignatureChannel',
        tax_exemption_type: this.typeValue,
        tax_exemption_id: taxExemptionId,
        signature_token: signatureToken,
      },
      {
        received: (data) => this.#onChannelReceived(data),
      }
    );
  }

  #unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  #onChannelReceived(data) {
    if (data.status === 'success' && data.e_sign_id) {
      this.#showStep('signature');
      try {
        initSignature(this.iframeContainerIdValue, data.e_sign_id, this.universignEndpointValue);
      } catch (err) {
        console.error('Universign init failed', err);
        this.#showStep('error');
      }
    } else {
      this.#showStep('error');
    }
  }

  #onPostMessage(event) {
    if (event.data?.type !== 'universign:complete') return;

    this.#unsubscribe();

    if (event.data.status === 'success') {
      this.#showStep('success');
      this.#refreshBlock();
    } else {
      this.#showStep('error');
    }
  }

  #refreshBlock() {
    fetch(this.refreshUrlValue, {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`refresh status ${r.status}`);

        return r.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBlock = doc.querySelector('[data-tax-exemption-signature-target="block"]');
        if (newBlock && this.hasBlockTarget) {
          this.blockTarget.replaceWith(newBlock);
        }
      })
      .catch((err) => {
        console.error('Refresh block failed', err);
        window.toastr?.info('Votre attestation a été signée. Veuillez rafraîchir la page.');
      });
  }

  #showStep(stepName) {
    const steps = {
      generation: this.hasGenerationStepTarget && this.generationStepTarget,
      signature: this.hasSignatureStepTarget && this.signatureStepTarget,
      success: this.hasSuccessStepTarget && this.successStepTarget,
      error: this.hasErrorStepTarget && this.errorStepTarget,
    };

    Object.entries(steps).forEach(([name, el]) => {
      if (!el) return;
      if (name === stepName) {
        el.classList.remove('d-none');
      } else {
        el.classList.add('d-none');
      }
    });
  }

  #getOffcanvas() {
    if (!this.hasDrawerTarget) return null;

    return Offcanvas.getOrCreateInstance(this.drawerTarget);
  }

  #drawers() {
    return [this.hasDrawerTarget && this.drawerTarget, this.hasInformationDrawerTarget && this.informationDrawerTarget]
      .filter(Boolean);
  }

  // Reopened with its errors when the social security information is invalid, focused on the first one
  #openInformationDrawer() {
    if (!this.hasInformationDrawerTarget) return;

    const drawer = this.informationDrawerTarget;
    drawer.addEventListener(
      'shown.bs.offcanvas',
      () => drawer.querySelector('[aria-invalid="true"]')?.focus(),
      { once: true }
    );
    Offcanvas.getOrCreateInstance(drawer).show();
  }

  // The parameter would otherwise start the signature again on each reload of the page
  #removeSignatureParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete('signature');
    window.history.replaceState(window.history.state, '', url);
  }

  #onDrawerHidden(event) {
    const focused = document.activeElement;
    if (focused && focused !== document.body && !event.target.contains(focused)) return;

    if (!this.hasBlockTarget) return;

    this.blockTarget.querySelector('button:not([disabled]), a[href]')?.focus();
  }
}
